import { useSearchParams } from 'next/navigation';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { FocusRingVariants, FontSizes, FontWeights, Modal, Text } from '@calm-web/design-system';

import Loader from '@/components/Loader';
import PaymentDetailsModal from '@/components/PreSignupFlow/Components/PaymentDetailsModal';
import PaymentError from '@/components/purchase/PaymentError';
import { SubscribePurchaseError, subscribePurchaseErrors } from '@/components/purchase/utils/Purchase';
import { useSimplifiedSignupContext } from '@/components/SimplifiedSignup/Context';
import { PaymentScreenConfig } from '@/components/SimplifiedSignup/Payment/types';
import { useAnalytics } from '@/hooks/analytics';
import { ProductInfo } from '@/hooks/analytics/types';
import { useGetPurchaseTerms } from '@/hooks/dtcLeadGenPartnership/useGetPurchaseTerms';
import { useFormattedPriceWithTax } from '@/hooks/purchase/useFormattedPriceWithTax';
import { usePlanSkuToggle } from '@/hooks/purchase/usePlanSkuToggle';
import {
	useAppLoadedState,
	usePartnerState,
	usePricesState,
	usePurchaseParamsState,
	useUserState,
} from '@/hooks/store';
import useIsCalmSleepApp from '@/hooks/useIsCalmSleepApp';
import { useSubscriptionDate } from '@/hooks/user';
import { useAmplitudeExperiment } from '@/hooks/utils/experiments/useAmplitudeExperiment';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import { getPrice, PlanCadence } from '@/store/purchaseParams/types';
import { AmplitudeExperimentNames } from '@/utils/experiments/amplitudeExperiment';
import { userCanTrial } from '@/utils/subscriptions';

import CheckoutSessionsForm from '../CheckoutSessionsForm';
import purchaseMessages from '../messages';
import PaymentElementsForm from '../PaymentElementsForm';
import PlanSkuToggle from '../PlanSkuToggle';
import PurchaseDetails from '../PurchaseDetails/refreshedPurchaseDetails';
import { CHECKOUT_SESSIONS_ELIGIBLE_FLOW_NAMES } from './constants';
import messages from './messages';
import {
	CTAContainer,
	HelpButton,
	HelpIcon,
	PurchaseFormSectionWrapper,
	SectionHeader,
	Wrapper,
} from './styles';

const PaymentElementsFormRefresh: React.FC<{
	endpoint: string | ((args: Record<string, unknown>) => string);
	purchaseArgs?: Record<string, unknown>;
	successArgs?: Record<string, unknown>;
	ignoreSubscribeErrors?: boolean;
	subscribePurchaseErrors?: SubscribePurchaseError[];
	analyticsPrefix?: string;
	analyticsEventProps?: Record<string, string>;
	purchaseTerms?: string;
	ctaCopy?: string | ((plan: string | undefined) => string | undefined);
	productInfo?: ProductInfo;
	afterPurchaseSuccess?: () => void;
	onPurchaseSuccess?: (
		response: Record<string, unknown>,
		purchaseArgs: Record<string, unknown>,
	) => Promise<void>;
	hidePaypal?: boolean;
	terms?: MessageDescriptor;
	children?: ReactNode;
	pageName?: string;
}> = ({
	ctaCopy,
	analyticsPrefix = 'Subscribe : Purchase : Form',
	analyticsEventProps,
	children,
	ignoreSubscribeErrors = false,
	hidePaypal = false,
	terms,
	endpoint,
	onPurchaseSuccess: onPurchaseSuccessOverride,
	afterPurchaseSuccess,
	productInfo: productInfoOverride,
	purchaseArgs = {},
	successArgs = {},
	subscribePurchaseErrors: subscribePurchaseErrorsOverride,
	pageName,
}) => {
	const purchaseParams = usePurchaseParamsState();
	const { purchaseType } = purchaseParams;
	const appLoaded = useAppLoadedState();
	const user = useUserState();
	const prices = usePricesState();
	const partner = usePartnerState();
	const partnerPurchaseTerms = useGetPurchaseTerms(partner?.offer_details);
	const { formatMessage, formatDate } = useIntl();
	const { logEvent } = useAnalytics();
	const searchParams = useSearchParams();
	const { skus } = usePlanSkuToggle();
	const isOrganicTraffic = searchParams?.get('utm_medium') !== 'paid';
	const isCalmSleepApp = useIsCalmSleepApp();
	const isPlanSkuToggleEnabled = isCalmSleepApp ? false : true;
	const { expireDate, premiumSubscriptionDaysRemaining } = useSubscriptionDate();
	const [isModalOpen, setIsModalOpen] = useState(false);

	const isFreeTrialInitial = userCanTrial(user) && purchaseType?.type === 'freetrial';
	const [isFreeTrial, setIsFreeTrial] = useState(isFreeTrialInitial);
	const {
		activeScreenConfig,
		hasFreeTrial = true,
		isCheckoutSessionsEnabled,
		name: flowName,
	} = useSimplifiedSignupContext();
	const {
		terms: configTerms,
		showToggle = true,
		trialTerms = messages.freeTrialTerms,
		noTrialTerms = messages.noTrialTerms,
		offerTerms,
		paymentFormHeader,
		paymentFormSubHeader,
		isRenewalOffer,
		hasTrueMedSupport,
		trialEndTerms,
		onPaymentFormSeen,
	} = activeScreenConfig as PaymentScreenConfig;

	const showPlanSkuToggle = typeof showToggle === 'function' ? showToggle({ prices }) : showToggle;

	// Swap only the inner payment form for the Stripe Checkout Sessions
	// form (keeping the plan toggle, purchase details, header, and terms above) when
	// enrolled in `www-checkout-sessions` AND on the default `subscription` path, OR on a
	// family purchase resolving to the stripe (not braintree/paypal) family endpoint — see
	// `isEligibleEndpoint` below — (excludes partner/renew/email) AND the flow is in
	// the `CHECKOUT_SESSIONS_ELIGIBLE_FLOW_NAMES` allowlist. The experiment is enrolled at
	// 100% treatment; production exposure is bounded by this conjunction plus the
	// allowlist, not by the experiment itself.
	//
	// Enrollment has two inputs: the SSR prop (`isCheckoutSessionsEnabled`, resolved in
	// pages/signup-flow.tsx) and the client Amplitude experiment. The SSR prop is often
	// evaluated pre-auth (no user_id / a different device_id), so it can diverge from the
	// client's live-identity read.
	const { isEnrolled: clientCheckoutSessionsEnrolled, isLoading: isCheckoutSessionsExperimentLoading } =
		useAmplitudeExperiment(AmplitudeExperimentNames.CHECKOUT_SESSIONS);
	// Latch the enrollment decision ONCE, on the first render where the payment form is
	// actually shown (once `appLoaded`, past the `<Loader />` gate below), so a mounted payment
	// form never swaps flavor mid-entry. We commit whichever value is available at that first
	// visible paint. `useAmplitudeExperiment` starts `isLoading: true` and only flips it in a
	// post-commit effect, so a fresh in-flow mount (the common case — `appLoaded` is already true
	// from an earlier screen) commits the SSR-optimistic value and keeps it for the life of the
	// form; the client value only wins when it has already resolved by first visible paint (e.g.
	// it resolved during the bootstrap loader window while `appLoaded` was still false). This
	// deliberately favors stability over honoring a late-resolving client value: swapping a form
	// the buyer is typing into loses their input and orphans the created Checkout Session. Trade-off
	// once the experiment is enabled: in-flow buyers bucket on the SSR value rather than a later
	// client re-read — intended per this ticket's "never changes flavor for the lifetime of the
	// payment screen" goal. Implemented as the react.dev "adjust state during render" one-shot:
	// synchronous, no extra paint, no swap window (an effect would leave a frame where the latch is
	// unset and the live value could flash the other form).
	const [latchedCheckoutSessionsEnrolled, setLatchedCheckoutSessionsEnrolled] = useState<boolean | null>(
		null,
	);
	let isCheckoutSessionsEnrolled = latchedCheckoutSessionsEnrolled ?? false;
	if (latchedCheckoutSessionsEnrolled === null && appLoaded) {
		isCheckoutSessionsEnrolled = isCheckoutSessionsExperimentLoading
			? Boolean(isCheckoutSessionsEnabled)
			: Boolean(clientCheckoutSessionsEnrolled);
		setLatchedCheckoutSessionsEnrolled(isCheckoutSessionsEnrolled);
	}
	// Latched at the same first visible paint as enrollment, so the buyer's own purchase making
	// their subscription valid never swaps the form underneath the success flow.
	const [latchedWasSubscribed, setLatchedWasSubscribed] = useState<boolean | null>(null);
	let wasSubscribedWhenShown = latchedWasSubscribed ?? false;
	if (latchedWasSubscribed === null && appLoaded) {
		wasSubscribedWhenShown = Boolean(user?.subscription?.valid);
		setLatchedWasSubscribed(wasSubscribedWhenShown);
	}

	const resolvedEndpoint = typeof endpoint === 'string' ? endpoint : endpoint({});
	const isLifetimePlan = purchaseParams?.plan === 'lifetime';
	// The session create returns 409 for anyone with a valid subscription, while the legacy
	// `subscription` endpoint upgrades a subscriber to Lifetime and cancels their renewing plan.
	const isLifetimeUpgrade = isLifetimePlan && wasSubscribedWhenShown;
	// Exclude TrueMed (HSA/FSA) buyers from the Checkout Sessions experiment: TrueMed is
	// `/subscription`-coupled via a `trueMedLink` the CS create can't carry, and the CS form
	// doesn't render the `TrueMedWidget` — so a TrueMed buyer stays on the legacy form. Full
	// CS + TrueMed support (needs a backend `trueMedLink` field) is tracked separately.
	// Defense-in-depth allowlist of the SimplifiedSignup flow identity —
	// see constants.ts. Keeps an Amplitude targeting widening from silently lighting up
	// CS on a surface that resolves `subscription` by coincidence but hasn't
	// been SSR-plumbed or QA'd for the CS form.
	const isCheckoutSessionsEligibleFlow = CHECKOUT_SESSIONS_ELIGIBLE_FLOW_NAMES.has(flowName);
	// A family purchase is the one other case allowed alongside the default `subscription`
	// endpoint: its `endpoint` prop is a function of the chosen payment method
	// (`familyplan/create/stripe` for card/wallet, `.../braintree` for paypal), and `resolvedEndpoint`
	// above always evaluates it with no args — so this only ever matches the stripe-resolving
	// (card/wallet) shape, never the braintree one. The API backend gates real family-SKU
	// acceptance server-side (a separate ticket); web can't and shouldn't read that flag, so
	// instead every family purchase attempts a Checkout Session and `checkoutSessionsRefused`
	// below latches a silent, one-time fallback to the legacy form on any refusal.
	const isFamilyPlan = purchaseParams?.plan === 'family';
	// The api only opens a one-time (Lifetime) session while its own server-side flag is on, so a
	// Lifetime purchase uses the same silent legacy fallback on refusal as family.
	const hasLegacyFallback = isFamilyPlan || isLifetimePlan;
	const isEligibleEndpoint =
		resolvedEndpoint === 'subscription' || (isFamilyPlan && resolvedEndpoint === 'familyplan/create/stripe');
	// Latches true the first time a family or Lifetime purchase's INITIAL Checkout Session creation
	// fails (see `onInitialCreateFailed` below) — never reset, so that selection falls back to
	// `PaymentElementsForm` for good within this mount rather than retrying on every toggle back
	// to it. Scoped to `hasLegacyFallback` in the conjunction below (not just checked bare): this
	// component renders `PlanSkuToggle`, which can flip `purchaseParams.plan` between `family` and
	// a CS-eligible plan like `yearly` WITHOUT remounting this component (toggling only dispatches
	// `setPurchaseParams`) — a refusal recorded while on `family` must not leak into a later,
	// never-refused `yearly` selection in the same mount.
	const [checkoutSessionsRefused, setCheckoutSessionsRefused] = useState(false);
	const useCheckoutSessions =
		isCheckoutSessionsEnrolled &&
		isEligibleEndpoint &&
		!isLifetimeUpgrade &&
		!hasTrueMedSupport &&
		isCheckoutSessionsEligibleFlow &&
		!(checkoutSessionsRefused && hasLegacyFallback);

	const [noSubscribeError] = useState(
		!ignoreSubscribeErrors && purchaseParams?.purchaseType?.type
			? (subscribePurchaseErrorsOverride ?? subscribePurchaseErrors).includes(
					purchaseParams.purchaseType.type as SubscribePurchaseError,
				)
			: false,
	);
	const planPriceWithTax = useFormattedPriceWithTax(
		getPrice(purchaseParams?.plan ?? 'Yearly', prices.current),
	);
	const originalPlanPriceWithTax = useFormattedPriceWithTax(
		getPrice(purchaseParams?.plan ?? 'Yearly', prices.original),
	);

	// Fires once, the first time this mount's initial Checkout Session creation fails — see
	// `checkoutSessionsRefused`'s own comment above. Only ever passed to `CheckoutSessionsForm`
	// for a family or Lifetime purchase (below), so every other plan keeps its existing
	// error+retry UI.
	const onCheckoutSessionCreateFailed = useCallback(() => setCheckoutSessionsRefused(true), []);

	function onTooltipClick(): void {
		setIsModalOpen(true);
		logEvent({
			eventName: 'Subscribe : Purchase : Form : Tool Tip : Clicked',
			eventProps: {
				is_stripe_next: true,
				tool_tip: 'why now',
			},
		});
	}

	function getTermsText(): MessageDescriptor {
		if (offerTerms && purchaseParams?.plan === 'lifetime') {
			return offerTerms;
		}

		if (purchaseParams?.plan === 'family') {
			return messages.familyTerms;
		}

		return getPrice(purchaseParams?.plan || 'yearly', prices.current) !==
			getPrice(purchaseParams?.plan || 'yearly', prices.original) && offerTerms
			? offerTerms
			: noTrialTerms;
	}

	useEffect(() => {
		function evalIsFreeTrial(): boolean {
			if (!hasFreeTrial) return false;
			if ((userCanTrial(user) && purchaseParams.plan === 'yearly') || purchaseType?.isFreeTrial) return true;
			return false;
		}
		// TODO: refactor this code to follow best practices
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setIsFreeTrial(evalIsFreeTrial());
	}, [purchaseParams.plan, purchaseType?.isFreeTrial, user, hasFreeTrial]);

	useComponentDidMount(() => {
		onPaymentFormSeen?.({ logEvent, prices, purchaseParams });
	});

	const planCadence = purchaseParams?.plan === 'family' ? 'yearly' : (purchaseParams?.plan ?? 'yearly');

	const buildTrialTerms = () => {
		const defaultPlaceholders = {
			price: planPriceWithTax,
			originalPrice: originalPlanPriceWithTax,
			cadence: formatMessage(messages[planCadence as PlanCadence]),
			cancelAnytimeLink: (...chunks: ReactNode[]) => (
				<a
					href="https://support.calm.com/hc/en-us/articles/115002473607-How-to-turn-off-auto-renewal-or-cancel-my-subscription"
					target="_blank"
					rel="noreferrer"
				>
					{chunks}
				</a>
			),
			a: (...chunks: ReactNode[]) => (
				<a href="/terms" target="_blank">
					{chunks}
				</a>
			),
			expireDate,
		};
		if (typeof trialTerms === 'function') {
			const { message, placeholders = {} } = trialTerms({ prices });
			return formatMessage(message, { ...defaultPlaceholders, ...placeholders });
		}
		return formatMessage(trialTerms, defaultPlaceholders);
	};

	if (!appLoaded) return <Loader />;

	if (noSubscribeError) {
		return <PaymentError errorType={purchaseParams?.purchaseType?.type as SubscribePurchaseError} />;
	}

	return (
		<Wrapper>
			<PurchaseFormSectionWrapper>
				{skus.length > 1 && showPlanSkuToggle && isOrganicTraffic && isPlanSkuToggleEnabled ? (
					<PlanSkuToggle terms={terms} />
				) : (
					<>
						<Text el="p" size={FontSizes.lg} weight={FontWeights.Medium}>
							{formatMessage(paymentFormHeader ?? messages.confirmPlanNoToggle, {
								trialLengthInDays: purchaseParams?.purchaseType?.duration || 14,
							})}
						</Text>
						<Text el="p" size={FontSizes.base}>
							{formatMessage(paymentFormSubHeader ?? messages.confirmSubtitle)}
						</Text>
						<PurchaseDetails isRenewalOffer={isRenewalOffer} trialEndTerms={trialEndTerms} />
						{configTerms && (
							<Text size={FontSizes.sm} color="blackAlpha60" el="p">
								{formatMessage(configTerms, {
									price: planPriceWithTax,
									cancelAnytimeLink: (...chunks: ReactNode[]) => (
										<a
											href="https://support.calm.com/hc/en-us/articles/115002473607-How-to-turn-off-auto-renewal-or-cancel-my-subscription"
											target="_blank"
											rel="noreferrer"
										>
											{chunks}
										</a>
									),
									originalPrice: originalPlanPriceWithTax,
									expireDate:
										expireDate &&
										formatDate(expireDate, {
											month: '2-digit',
											year: 'numeric',
										}),
								})}
							</Text>
						)}
					</>
				)}
			</PurchaseFormSectionWrapper>
			<PurchaseFormSectionWrapper>
				<SectionHeader>
					<Text size={FontSizes.lg} weight={FontWeights.Medium}>
						{formatMessage(messages.paymentMethodHeader)}
					</Text>
					{isFreeTrial && (
						<HelpButton
							aria-label={formatMessage(purchaseMessages.cvvHelpModalTitle)}
							onPress={onTooltipClick}
							Icon={HelpIcon}
							focusRingVariant={FocusRingVariants.Dark}
						/>
					)}
				</SectionHeader>
				{useCheckoutSessions ? (
					<CheckoutSessionsForm
						productInfo={productInfoOverride}
						successArgs={successArgs}
						purchaseArgs={purchaseArgs}
						ctaCopy={ctaCopy}
						afterPurchaseSuccess={afterPurchaseSuccess}
						hidePaypal={hidePaypal}
						// Threaded through so CheckoutSessionsForm's own PayPal (Braintree) submit —
						// a classic POST outside the session — resolves to the SAME endpoint the
						// legacy branch below would use (e.g. `familyplan/create/braintree` for a
						// family PayPal purchase), instead of assuming the default `subscription`.
						endpoint={endpoint}
						analyticsPrefix={analyticsPrefix}
						analyticsEventProps={analyticsEventProps}
						onInitialCreateFailed={hasLegacyFallback ? onCheckoutSessionCreateFailed : undefined}
					/>
				) : (
					<PaymentElementsForm
						hasTrueMedSupport={hasTrueMedSupport}
						analyticsPrefix={analyticsPrefix}
						analyticsEventProps={analyticsEventProps}
						endpoint={endpoint}
						ctaCopy={ctaCopy}
						hidePaypal={hidePaypal}
						onPurchaseSuccess={onPurchaseSuccessOverride}
						afterPurchaseSuccess={afterPurchaseSuccess}
						productInfo={productInfoOverride}
						purchaseArgs={purchaseArgs}
						successArgs={successArgs}
						subscribePurchaseErrors={subscribePurchaseErrorsOverride}
						ignoreSubscribeErrors={ignoreSubscribeErrors}
						hideTerms
						pageName={pageName}
					/>
				)}
			</PurchaseFormSectionWrapper>
			{children}
			<CTAContainer>
				<Text
					size={FontSizes.sm}
					color="blackAlpha60"
					el="p"
					data-testid="payment-elements-form-refresh-trial-terms"
				>
					{partner ? (
						<>
							{partnerPurchaseTerms}
							<br />
							<br />
							{partner?.offer_details?.terms || ''}
						</>
					) : isFreeTrial ? (
						<>{buildTrialTerms()}</>
					) : (
						formatMessage(getTermsText(), {
							price: planPriceWithTax,
							cancelAnytimeLink: (...chunks: ReactNode[]) => (
								<a
									href="https://support.calm.com/hc/en-us/articles/115002473607-How-to-turn-off-auto-renewal-or-cancel-my-subscription"
									target="_blank"
									rel="noreferrer"
								>
									{chunks}
								</a>
							),
							terms: (...chunks: ReactNode[]) => (
								<a href="/terms" target="_blank">
									{chunks}
								</a>
							),
							bold: (...chunks) => <b>{chunks}</b>,
							cadence: formatMessage(messages[planCadence as PlanCadence]),
							originalPrice: originalPlanPriceWithTax,
							expireDate:
								expireDate &&
								formatDate(expireDate, {
									dateStyle: 'short',
								}),
							premiumSubscriptionDaysRemaining,
						})
					)}
				</Text>
			</CTAContainer>
			<Modal isOpen={isModalOpen} closeModal={(): void => setIsModalOpen(false)} canClose aria-label={''}>
				<PaymentDetailsModal onCloseClick={(): void => setIsModalOpen(false)} />
			</Modal>
		</Wrapper>
	);
};

export default PaymentElementsFormRefresh;
